import * as React from 'react';

import {
  withSystem,
  TypeImplementation,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {TText, TList, EText, EList} from '@packages/systems/wfdl-ast';

import {createSelector} from 'reselect';
import {VisuallyHidden} from '@packages/systems/spring';
import TooltipControl from '@packages/systems/design-system/TooltipControl';
import {styledDiv, styled} from '@packages/utilities/styled';
import {
  LogicLabel,
  SearchInputPopoverList,
  RenderMatch,
  RenderItemLabelHelper,
} from '@packages/systems/flows/shared';
import {Users12} from '@packages/systems/ui/icons';

import {StyledHelpIcon} from './icons';
// eslint-disable-next-line webflow/module-top-level-imports
import {ItemWithId} from '@packages/systems/flows/shared/components/SearchInputPopoverList';

import {stripAll} from '@packages/utilities/contextual-expression';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';

const tooltipTextAccessGroups = 'The access groups this user will be added to.';
const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56, // 224
}));

const typeOfAccessGroupsAlias = withSystem(
  {
    isFullWidth: true,
    label: 'Access groups',
    labelVariant: 'hidden',
    bindUIVariant: 'custom',
  },
  TList(TText)
);

// @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'.
const getAccessGroupLabel = (group: ItemWithId<never>) => group.name;
const getAccessGroupTierLabel = (group: ItemWithId<never>) =>
  // @ts-expect-error - TS2339 - Property 'paid' does not exist on type 'never'.
  group.paid ? 'paid access' : undefined;

const filterIsNotId = (selectedId: string) =>
  matchExpression({
    Text: (id) => id !== selectedId,
    _: () => true,
  });

const ALL_ACCESS_GROUPS = 'all_access_groups';
const allOptionConfig = {
  id: ALL_ACCESS_GROUPS,
  icon: <Users12 />,
  label: 'All groups',
} as const;
const ecommPlans = new Set([
  'ecommerce_standard',
  'ecommerce_plus',
  'ecommerce_advanced',
]);
const selectSitePlan = (state: any) =>
  state.SiteDataStore.siteSubscription.type;

export const AccessGroupsAliasType = TypeImplementation.Alias({
  type: typeOfAccessGroupsAlias,
  system: {bindUIVariant: 'custom'},
  render: function RenderAccessGroupsAlias({expr, dispatch}) {
    const accessGroups = useDesignerSelector(selectAccess) as Array<{
      id: string;
      name: string;
      slug: string;
      paid: boolean;
    }>;
    const sitePlanType = useDesignerSelector(selectSitePlan);
    const isEcommercePlan = ecommPlans.has(sitePlanType);

    const selectedAccessGroups = React.useMemo(
      () => (expr ? Expressions.getList(stripAll(expr)) || [] : []),
      [expr]
    );

    const searchList = React.useMemo(() => {
      const result: Array<ItemWithId<never>> = [];

      const listSet = new Set(selectedAccessGroups.map(Expressions.getText));

      for (const group of accessGroups) {
        if (!listSet.has(group.id)) {
          if (isEcommercePlan) {
            // @ts-expect-error - TS2345 - Argument of type '{ id: string; name: string; slug: string; paid: boolean; }' is not assignable to parameter of type 'never'.
            result.push(group);
          } else if (!isEcommercePlan && !group.paid) {
            // @ts-expect-error - TS2345 - Argument of type '{ id: string; name: string; slug: string; paid: boolean; }' is not assignable to parameter of type 'never'.
            result.push(group);
          }
        }
      }
      return result;
    }, [selectedAccessGroups, accessGroups, isEcommercePlan]);

    const onAccessGroupSelect = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'group' implicitly has an 'any' type.
      (group) => {
        if (group.id === ALL_ACCESS_GROUPS) {
          const groupIds = accessGroups.reduce<Array<any>>(
            (acc, accessGroup) => {
              if (isEcommercePlan) {
                acc.push(EText(accessGroup.id));
              } else if (!isEcommercePlan && !accessGroup.paid) {
                acc.push(EText(accessGroup.id));
              }
              return acc;
            },
            []
          );
          dispatch(updateExpressionAction(EList(groupIds)));
        } else {
          dispatch(
            updateExpressionAction(
              EList(selectedAccessGroups.concat(EText(group.id.toString())))
            )
          );
        }
      },
      [dispatch, selectedAccessGroups, accessGroups, isEcommercePlan]
    );

    const onAccessGroupRemove = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'removeId' implicitly has an 'any' type.
      (removeId) => {
        const updatedList = selectedAccessGroups.filter(
          filterIsNotId(removeId)
        );

        dispatch(updateExpressionAction(EList(updatedList)));
      },
      [dispatch, selectedAccessGroups]
    );

    const itemsSelected = React.useMemo(() => {
      const result: Array<ItemWithId<never>> = [];

      for (const textExpr of selectedAccessGroups) {
        const id = Expressions.getText(textExpr);
        if (id == null) continue;
        const accessGroup = accessGroups.find((el) => el.id === id);
        // @ts-expect-error - TS2345 - Argument of type '{ id: string; name: string; slug: string; paid: boolean; }' is not assignable to parameter of type 'never'.
        accessGroup && result.push({...accessGroup, id: accessGroup.id});
      }

      return result;
    }, [accessGroups, selectedAccessGroups]);

    return (
      <div>
        <LogicLabel label="Access Groups">
          <VisuallyHidden>{tooltipTextAccessGroups}</VisuallyHidden>
          {/* @ts-expect-error - TS2739 - Type '{ "aria-hidden": true; icon: Element; content: Element; alternate: true; }' is missing the following properties from type '{ [x: string]: any; iconStyle?: { padding: number; boxSizing: string; } | undefined; icon?: Element | undefined; cursor?: string | undefined; placement?: string | undefined; children: any; hideOnClick: any; style: any; automationLabel?: string | undefined; }': children, hideOnClick, style */}
          <TooltipControl
            aria-hidden
            icon={<StyledHelpIconPadded />}
            content={<TooltipContent>{tooltipTextAccessGroups}</TooltipContent>}
            alternate
          />
        </LogicLabel>

        <SearchInputPopoverList
          searchList={searchList}
          onItemSelect={onAccessGroupSelect}
          allOptionConfig={allOptionConfig}
          getSearchableText={getAccessGroupLabel}
          itemTypeLabel="access groups"
          renderMatch={({preselected, match}) => {
            const secondaryLabel = getAccessGroupTierLabel(match);
            const labels = [
              {
                label: `${getAccessGroupLabel(match)} `,
              },
            ];

            return (
              <RenderMatch
                highlight={preselected === match}
                labels={
                  secondaryLabel
                    ? [
                        ...labels,
                        {
                          isSecondary: true,
                          label: `(${secondaryLabel})`,
                        },
                      ]
                    : labels
                }
              />
            );
          }}
          selectedItemsConfig={{
            name: 'access-groups-list',
            onItemRemoved: onAccessGroupRemove,
            items: itemsSelected,
            renderPropertyListItemLabel: (item) => {
              const secondaryLabel = getAccessGroupTierLabel(item);
              return (
                <RenderItemLabelHelper
                  // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'.
                  primaryLabel={item.name}
                  secondaryLabel={secondaryLabel ? secondaryLabel : undefined}
                />
              );
            },
          }}
        />
      </div>
    );
  },
});

const selectAccess = createSelector(
  (state: DesignerStores) => state.SiteUsersStore.accessGroups.groups.value,
  (groupsValue) =>
    groupsValue.map(({_id, name, slug, access}) => ({
      id: _id,
      name,
      slug,
      paid: access.commerce.products?.length > 0,
    }))
);

const StyledHelpIconPadded = styled(StyledHelpIcon)(({theme: {gutters}}) => ({
  paddingLeft: gutters.x4,
}));
